import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Card,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Typography,
} from '@mui/material';

import TimeSettingsCheckboxes from 'components/domain/settings/TimeSettingsCheckboxes.js';
import { updateTimeSettings } from 'store/reducers/inputReducer';
import { selectTimeSetting } from 'store/reducers/selectors';

import { STATIC_TABLE_SETTINGS } from 'store/reducers/constants/constants.js';

export default function TimeSettingsDialog(props) {
  const { open, tableName, onClose } = props;

  const timeUnit = useSelector(selectTimeSetting('timeUnit'));
  const staticTableSettings = STATIC_TABLE_SETTINGS?.[tableName];
  const timePeriodSpansDays = useSelector(selectTimeSetting('timePeriodSpansDays'));
  const usePickupEnd = useSelector(selectTimeSetting('usePickupEnd', tableName));
  const usePickupStart = useSelector(selectTimeSetting('usePickupStart', tableName));
  const usePickupExact = useSelector(selectTimeSetting('usePickupExact', tableName));
  const useDropoffEnd = useSelector(selectTimeSetting('useDropoffEnd', tableName));
  const useDropoffStart = useSelector(selectTimeSetting('useDropoffStart', tableName));
  const useDropoffExact = useSelector(selectTimeSetting('useDropoffExact', tableName));
  const timeUseTimeWindows = useSelector(selectTimeSetting('useTimeWindows', tableName));

  // table_settings_static.json stores time window config at STATIC_TABLE_SETTINGS[tableName].timeWindows
  const tableTimeSettings = tableName ? staticTableSettings?.timeWindows : null;

  const dispatch = useDispatch();

  const handleClose = () => {
    if (typeof onClose === 'function') onClose();
  };

  const handleTimeUnitChange = (event) => {
    const columnNames = [];
    const columnValues = [];
    const value = event.target.value;
    
    if (value === 'integer') {
      if (tableTimeSettings?.pickupExact) {
        columnNames.push(tableTimeSettings.pickupExact.columnNameInt);
        if (usePickupExact) {
          columnValues.push(true);
          columnNames.push(tableTimeSettings.pickupExact.columnNameTime);
          columnValues.push(false);
        } else {
          columnValues.push(false);
        }
      }

      if (tableTimeSettings?.pickupStart) {
        columnNames.push(tableTimeSettings.pickupStart.columnNameInt);
        columnValues.push(Boolean(usePickupStart));
      }

      if (tableTimeSettings?.pickupEnd) {
        columnNames.push(tableTimeSettings.pickupEnd.columnNameInt);
        columnValues.push(Boolean(usePickupEnd));
      }

      if (tableTimeSettings?.dropoffExact) {
        columnNames.push(tableTimeSettings.dropoffExact.columnNameInt);
        if (useDropoffExact) {
          columnValues.push(true);
          columnNames.push(tableTimeSettings.dropoffExact.columnNameTime);
          columnValues.push(false);
        } else {
          columnValues.push(false);
        }
      }

      if (tableTimeSettings?.dropoffStart) {
        columnNames.push(tableTimeSettings.dropoffStart.columnNameInt);
        columnValues.push(Boolean(useDropoffStart));
      }

      if (tableTimeSettings?.dropoffEnd) {
        columnNames.push(tableTimeSettings.dropoffEnd.columnNameInt);
        columnValues.push(Boolean(useDropoffEnd));
      }
    }

    if (value === 'clock') {
      if (tableTimeSettings?.pickupExact) {
        columnNames.push(tableTimeSettings.pickupExact.columnNameTime);
        if (usePickupExact) {
          columnValues.push(true);
          columnNames.push(tableTimeSettings.pickupExact.columnNameInt);
          columnValues.push(false);
        } else {
          columnValues.push(false);
        }
      }

      if (tableTimeSettings?.pickupStart) {
        columnNames.push(tableTimeSettings.pickupStart.columnNameTime);
        columnValues.push(Boolean(usePickupStart));
      }

      if (tableTimeSettings?.pickupEnd) {
        columnNames.push(tableTimeSettings.pickupEnd.columnNameTime);
        columnValues.push(Boolean(usePickupEnd));
      }

      if (tableTimeSettings?.dropoffExact) {
        columnNames.push(tableTimeSettings.dropoffExact.columnNameTime);
        if (useDropoffExact) {
          columnValues.push(true);
          columnNames.push(tableTimeSettings.dropoffExact.columnNameInt);
          columnValues.push(false);
        } else {
          columnValues.push(false);
        }
      }

      if (tableTimeSettings?.dropoffStart) {
        columnNames.push(tableTimeSettings.dropoffStart.columnNameTime);
        columnValues.push(Boolean(useDropoffStart));
      }

      if (tableTimeSettings?.dropoffEnd) {
        columnNames.push(tableTimeSettings.dropoffEnd.columnNameTime);
        columnValues.push(Boolean(useDropoffEnd));
      }
    }
    
    dispatch(updateTimeSettings({ columnNames, columnValues, settingNames: ['timeUnit'], settingValues: [value], tableName }));
  };

  const handleUseTimeWindowsChange = () => {
    dispatch(updateTimeSettings({
      settingNames: ['useTimeWindows'],
      tableName,
      settingValues: [!timeUseTimeWindows],
    }));
  };

  const handleTimePeriodSpanChange = () => {
    dispatch(updateTimeSettings({
      settingNames: ['timePeriodSpansDays'],
      tableName,
      settingValues: [!timePeriodSpansDays],
    }));
  };

  if (!tableTimeSettings) return null;

  return (
    <Dialog
      aria-labelledby="time-settings-dialog-title"
      open={Boolean(open)}
      onClose={handleClose}
      onClick={(event) => event.stopPropagation()}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 20px 60px rgba(23, 43, 77, 0.18)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        id="time-settings-dialog-title"
        sx={{
          px: 2,
          py: 1.75,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          background:
            'linear-gradient(180deg, rgba(230, 252, 251, 0.55) 0%, rgba(255, 255, 255, 1) 100%)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'primary.extraLight',
              color: 'primary.main',
              boxShadow: '0 8px 20px rgba(0, 146, 139, 0.12)',
              flexShrink: 0,
            }}
          >
            <AccessTimeRoundedIcon sx={{ fontSize: '1.15rem' }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 800, color: 'dark.main', lineHeight: 1.15 }}
              noWrap
            >
              Time settings
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.15 }}
              noWrap
            >
              {tableTimeSettings?.description || 'Configure time windows for this table'}
            </Typography>
          </Box>
        </Box>

        <IconButton
          aria-label="Close"
          onClick={handleClose}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'rgba(0, 146, 139, 0.08)',
              color: 'primary.main',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ px: 2.5, py: 2.25 }}>
        <Stack spacing={2.25} sx={{ minHeight: 320 }}>
          <Card
            variant="outlined"
            sx={{
              borderRadius: 2.5,
              borderColor: 'divider',
              boxShadow: '0 4px 14px rgba(0,0,0,0.03)',
            }}
          >
            <Stack spacing={1.25} sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'dark.main' }}>
                    Enable time windows
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Turn on time windows to set pickup and dropoff constraints.
                  </Typography>
                </Box>
                <Switch checked={Boolean(timeUseTimeWindows)} onChange={handleUseTimeWindowsChange} />
              </Box>
            </Stack>
          </Card>

          <Collapse in={Boolean(timeUseTimeWindows)} unmountOnExit>
            <Stack spacing={2.25}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 2.5,
                  borderColor: 'divider',
                  boxShadow: '0 4px 14px rgba(0,0,0,0.03)',
                }}
              >
                <Stack spacing={1.25} sx={{ p: 2 }}>
                  <FormControl>
                    <FormLabel sx={{ fontWeight: 800, color: 'dark.main' }}>Time format</FormLabel>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.75 }}>
                      Choose how time values are represented in your table.
                    </Typography>
                    <RadioGroup value={timeUnit} onChange={handleTimeUnitChange}>
                      <FormControlLabel
                        value="clock"
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                              Clock time
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              Example: 11:00 am
                            </Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value="integer"
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                              Integer
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              Example: 660 minutes
                            </Typography>
                          </Box>
                        }
                      />
                    </RadioGroup>
                  </FormControl>

                  <Collapse in={timeUnit === 'clock'} unmountOnExit>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'dark.main' }}>
                          Time period spans multiple days
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Enable if time windows may cross midnight.
                        </Typography>
                      </Box>
                      <Switch checked={Boolean(timePeriodSpansDays)} onChange={handleTimePeriodSpanChange} />
                    </Box>
                  </Collapse>
                </Stack>
              </Card>

              <Card
                variant="outlined"
                sx={{
                  borderRadius: 2.5,
                  borderColor: 'divider',
                  boxShadow: '0 4px 14px rgba(0,0,0,0.03)',
                }}
              >
                <Stack spacing={1.25} sx={{ p: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'dark.main' }}>
                      Active time windows
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Toggle which time-window columns are visible and used.
                    </Typography>
                  </Box>
                  <Divider />
                  <TimeSettingsCheckboxes tableName={tableName} />
                </Stack>
              </Card>
            </Stack>
          </Collapse>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 2, gap: 1, justifyContent: 'flex-end' }}>
        <Button
          onClick={handleClose}
          variant="contained"
          sx={{
            color: '#fff',
            backgroundColor: 'primary.main',
            borderColor: 'primary.main',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { borderColor: 'primary.extraDark', backgroundColor: 'primary.extraDark' },
          }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

TimeSettingsDialog.propTypes = {
  onClose: PropTypes.func,
  open: PropTypes.bool.isRequired,
  tableName: PropTypes.string,
};
